import { appHref } from './globals.js';

/**
 * Where this person last was, so signing in puts them back there.
 *
 * Per user and per DEVICE, for the same reason the theme is: this is a fact
 * about a browser somebody left open, not about the person. The laptop they
 * do the books on and the phone they check a balance with are rarely in the
 * middle of the same thing, and making one dictate the other's starting
 * point would be worse than starting both at the apps list.
 *
 * Deliberately NOT in `webledgerPrefs`, which is server-synced and is the
 * right home for "which currency did I last use". That store belongs to one
 * app, and this records a path that may be in any of them — a cross-app fact
 * has no business in one app's table.
 *
 * Only the path is kept. The filters a page shows are already remembered by
 * the page itself, in the places that own them (`PREF.walletsSection`,
 * `PREF.lastAccount`, the report filters, the scroll keys), and recording
 * them a second time here would be a copy to keep in step with no way to
 * know which one was right.
 */

const KEY = (userId: string): string => `colplat:last-place:${userId}`;

/**
 * Paths that must never be returned to.
 *
 * `/login` would bounce a signed-in person straight back out of the app, and
 * `/change-password` is somewhere you are sent rather than somewhere you
 * were — landing on it a week later reads as the app demanding something.
 */
const NEVER_RETURN = ['/login', '/change-password', '/'];

export function rememberPlace(userId: string | undefined, path: string): void {
  if (!userId || NEVER_RETURN.includes(path)) return;
  try {
    localStorage.setItem(KEY(userId), path);
  } catch {
    // Private windows and blocked site data both throw. Forgetting where
    // somebody was is a small loss; failing to show them the page is not.
  }
}

/** The path to open after signing in, or the launcher when there is none. */
export function placeToResume(userId: string | undefined): string {
  if (!userId) return appHref('/');
  try {
    const path = localStorage.getItem(KEY(userId));
    // Checked again on the way out, not only on the way in: a path stored by
    // an older build, or by a version of this app whose pages have since been
    // renamed, must not strand anybody.
    if (!path || !path.startsWith('/') || NEVER_RETURN.includes(path)) return appHref('/');
    return appHref(path);
  } catch {
    return appHref('/');
  }
}

export function forgetPlace(userId: string | undefined): void {
  if (!userId) return;
  try {
    localStorage.removeItem(KEY(userId));
  } catch { /* nothing to forget. */ }
}
